import { THREE } from '../../../lib/three-client.js';
import {
  createParcelMesh,
  createParcelOutline,
  createElectricLine,
  createSideWalls,
  fitCameraToObject,
  toThreePos,
} from '../../parcelSlope/ParcelSlopeThreeCard/createParcelMesh.js';

function sampleNearestVertexZ(vertices, x, y) {
  let bestZ = null;
  let bestD = Infinity;
  for (const v of vertices || []) {
    const vx = Number(v.x);
    const vy = Number(v.y);
    const vz = Number(v.z);
    if (!Number.isFinite(vx) || !Number.isFinite(vy) || !Number.isFinite(vz)) continue;
    const d = (vx - x) ** 2 + (vy - y) ** 2;
    if (d < bestD) {
      bestD = d;
      bestZ = vz;
    }
  }
  return bestZ;
}

/** Kenar boyunca ara noktalar — yuzey kotuna daha sik oturur. */
function densifyRingXy(ptsRaw, step = 2.0) {
  if (!Array.isArray(ptsRaw) || ptsRaw.length < 2) return [];
  const ring = ptsRaw.map((p) => ({
    x: Number(p.x) || 0,
    y: Number(p.y) || 0,
  }));
  const closed = ring.length > 1
    && Math.hypot(ring[0].x - ring[ring.length - 1].x, ring[0].y - ring[ring.length - 1].y) < 1e-6;
  const n = closed ? ring.length - 1 : ring.length;
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    out.push({ x: a.x, y: a.y });
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) continue;
    const count = Math.max(0, Math.floor(len / step) - 1);
    for (let k = 1; k <= count; k += 1) {
      const t = k / (count + 1);
      out.push({ x: a.x + dx * t, y: a.y + dy * t });
    }
  }
  return out;
}

function sampleOutlinePoints(data, surfaceMesh) {
  const ptsRaw = data?.parcel_outline || [];
  if (!surfaceMesh || ptsRaw.length < 3) return null;

  const { minZ, heightScale, vertices } = surfaceMesh.userData;
  const densified = densifyRingXy(ptsRaw, 2.0);
  if (densified.length < 3) return null;

  surfaceMesh.updateMatrixWorld(true);
  const raycaster = new THREE.Raycaster();
  const down = new THREE.Vector3(0, -1, 0);
  const eps = 0.08;

  const pts = [];
  for (const p of densified) {
    const lx = p.x;
    const ly = p.y;
    const origin = new THREE.Vector3(lx, 8000, -ly);
    raycaster.set(origin, down);
    const hits = raycaster.intersectObject(surfaceMesh, false);

    let threeY;
    if (hits.length > 0 && Number.isFinite(hits[0].point.y)) {
      threeY = hits[0].point.y + eps;
    } else {
      const zSurf = sampleNearestVertexZ(vertices, lx, ly);
      if (!Number.isFinite(zSurf)) continue;
      threeY = heightScale * (zSurf - minZ) + eps;
    }
    pts.push(new THREE.Vector3(lx, threeY, -ly));
  }

  if (pts.length < 3) return null;
  return pts;
}

/**
 * Orijinal parsel siniri — buffer mesh yuzey kotuna oturur.
 * Ic dolgu kirmizi; kenar tek kirmizi tube (siyah alt cizgi yok).
 */
export function createInnerParcelOutline(data, surfaceMesh) {
  const pts = sampleOutlinePoints(data, surfaceMesh);
  if (!pts || pts.length < 3) return null;

  const group = new THREE.Group();
  group.name = 'innerParcelOutline';
  group.renderOrder = 3;

  const contour = pts.map((p) => new THREE.Vector2(p.x, -p.z));
  const faces = THREE.ShapeUtils.triangulateShape(contour, []);
  if (faces.length > 0) {
    const positions = new Float32Array(pts.length * 3);
    for (let i = 0; i < pts.length; i += 1) {
      positions[i * 3] = pts[i].x;
      positions[i * 3 + 1] = pts[i].y;
      positions[i * 3 + 2] = pts[i].z;
    }
    const indices = [];
    for (const face of faces) {
      if (!Array.isArray(face) || face.length < 3) continue;
      indices.push(face[0], face[1], face[2]);
    }
    if (indices.length >= 3) {
      const fillGeo = new THREE.BufferGeometry();
      fillGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      fillGeo.setIndex(indices);
      fillGeo.computeVertexNormals();
      const fillMat = new THREE.MeshBasicMaterial({
        color: 0xef4444,
        side: THREE.DoubleSide,
        depthTest: true,
        depthWrite: false,
      });
      const fill = new THREE.Mesh(fillGeo, fillMat);
      fill.renderOrder = 3;
      fill.name = 'innerParcelFill';
      group.add(fill);
    }
  }

  const closed = pts.slice();
  closed.push(pts[0].clone());
  const curve = new THREE.CatmullRomCurve3(closed, true, 'catmullrom', 0.0);
  const tubularSegments = Math.max(closed.length * 3, 48);
  const edgeGeo = new THREE.TubeGeometry(curve, tubularSegments, 0.28, 8, true);
  const edgeMat = new THREE.MeshBasicMaterial({
    color: 0xef4444,
    depthTest: true,
    depthWrite: false,
  });
  const edge = new THREE.Mesh(edgeGeo, edgeMat);
  edge.renderOrder = 4;
  edge.name = 'innerParcelOutlineEdge';
  group.add(edge);

  return group;
}

export {
  createParcelMesh,
  createParcelOutline,
  createElectricLine,
  createSideWalls,
  fitCameraToObject,
  toThreePos,
};
